/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Col, Row } from "react-awesome-styled-grid";
import { FormProvider, useFormContext } from "react-hook-form";
import { Heading, Label, Text, theme } from "@nordcloud/gnui";
import { CloudAccountCloudProvider } from "~/generated/graphql";
import { FormGroup, TransformableInput, StepContainer } from "~/components";
import { ibmRegionsOptions } from "~/constants";
import { FormDataIbm, FormField } from "../formConfig";
import { AccountDetailsFields } from "./AccountDetailsFields";
import { AccountRegionsSelect } from "./AccountRegionsSelect";
import { IBM_INFO_ONBOARDING, URL_IBM_CLOUD_PLATFORM } from "./constants";
import { trimTransform } from "./utils";

type Props = {
  nextStep: () => void;
};

export function AccountDetailsFormIbm({ nextStep }: Props) {
  const formMethods = useFormContext<FormDataIbm>();
  const {
    formState: { errors, isValid },
    trigger,
    handleSubmit,
    control,
  } = formMethods;

  const submit = () => {
    trigger();
    if (isValid) {
      nextStep();
    }
  };

  return (
    <Row justify="center">
      <Col sm={6} md={6} lg={8}>
        <Row>
          <Col>
            <AccountDetailsFields provider={CloudAccountCloudProvider.Ibm} />
            <AccountRegionsSelect options={ibmRegionsOptions} />
            <Heading level={3} marginBottom="spacing05">
              Account Credentials
            </Heading>
            <Text color={theme.color.text.text02}>{IBM_INFO_ONBOARDING}</Text>
            <FormProvider {...formMethods}>
              <form id="addAccountForm" onSubmit={handleSubmit(submit)}>
                <StepContainer counter={1}>
                  <Text>
                    Sign in to{" "}
                    <a
                      href={URL_IBM_CLOUD_PLATFORM}
                      target="_blank"
                      rel="noreferrer"
                    >
                      IBM Cloud Platform
                    </a>{" "}
                    with the credentials of the account.
                  </Text>
                  <FormGroup error={errors[FormField.providerId]}>
                    <Label
                      htmlFor={FormField.providerId}
                      name="Provide IBM Cloud Account ID"
                      required
                    />
                    <TransformableInput
                      name={FormField.providerId}
                      transform={trimTransform}
                      control={control}
                      placeholder="a1b2c3d4e5f61234567890fedcba4321"
                    />
                  </FormGroup>
                </StepContainer>

                <StepContainer counter={2}>
                  <FormGroup error={errors[FormField.apiKey]}>
                    <Label htmlFor={FormField.apiKey} name="API Key" required />
                    <TransformableInput
                      type="password"
                      name={FormField.apiKey}
                      transform={trimTransform}
                      control={control}
                      placeholder="e.g., XEogykgnehnuMKZUEM-abFxjK7uCNF9FOdBRI9-KgTMR"
                    />
                  </FormGroup>
                </StepContainer>
              </form>
            </FormProvider>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
